/* eslint-disable no-invalid-this */
import { diffArrays } from '../utils/diffArray';
import { encryptToSha256 } from '../utils/encryption';

function getItem(key) {
  if (typeof window === 'undefined') {
    return null;
  }
  return window.localStorage.getItem(key);
}

function removeItem(key) {
  if (typeof window === 'undefined') {
    return null;
  }
  return window.localStorage.removeItem(key);
}

function setItem(key, value) {
  if (typeof window === 'undefined') {
    return null;
  }
  return window.localStorage.setItem(key, value);
}

const checkPathName = (term) => {
  if (typeof window === 'undefined') {
    return false;
  }
  return window.location.pathname.includes(term);
};

const formatDate = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  let month = String(d.getMonth() + 1);
  let day = String(d.getDate());

  if (month.length < 2) month = '0' + month;
  if (day.length < 2) day = '0' + day;

  return [year, month, day].join('-');
};

export const formatPrice = (price) => {
  return price ? (Number(price) / 100).toFixed(2) : price;
};

const getCurrentPrice = (product) => {
  const price = product.discountedPrice ? product.discountedPrice : product.price;
  return formatPrice(price);
};

const MAX_PRODUCTS_PER_PUSH = 12;

const batchItems = (items) => {
  return items?.reduce((resultsItems, item, i) => {
    const chunkIndex = Math.floor(i / MAX_PRODUCTS_PER_PUSH);

    if (!resultsItems[chunkIndex]) resultsItems[chunkIndex] = [];

    resultsItems[chunkIndex].push(item);

    return resultsItems;
  }, []);
};

const getAge = (dateString) => {
  let today = new Date();
  let birthDate = new Date(dateString);
  let age = today.getFullYear() - birthDate.getFullYear();
  let m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

const getPageType = (nodeType) => {
  if (window.location.pathname === '/') {
    return 'home';
  }

  if (nodeType === 'product') {
    return 'product';
  }

  switch (true) {
    case checkPathName('/categories'):
      return 'category';
    case checkPathName('/q/'):
      return 'search';
    case checkPathName('/cart-page'):
    case checkPathName('/checkout/cart'):
      return 'cart';
    case checkPathName('/checkout/checkout'):
      return 'checkout';
    case checkPathName('/checkout/finished'):
      return 'orderConfirmation';
    case checkPathName('/account'):
      return 'myAccount';
    case checkPathName('/content/'):
      return 'content';
    case checkPathName('/help/'):
      return 'help & faqs';
    default:
      return 'other';
  }
};

const getHasSalePrice = (prices) => {
  return prices?.some((x) => x?.custom?.price_type?.includes('sale'));
};

class TagManager {
  constructor(dataLayerIdentifier = 'dataLayer') {
    this.dataLayerIdentifier = dataLayerIdentifier;
  }

  virtualPageView = async (data) => {
    const now = new Date();
    const timestampNow = now.getTime();
    const randomInt = Math.floor(Math.random() * 100000000000000);
    (window.dataLayer ??= []).push({
      event: 'eventIDGenerated',
      timestampNow: timestampNow,
      randomInt: randomInt,
    });

    if (!data) return;
    const { pageData, userData, customUrl } = data;
    if (!pageData || !userData) return; // if we are missing any of the required objects then do nothing
    // @TODO: need to fix for redirect
    const userDevice =
      screen.width < 500 ||
      navigator.userAgent.match(/Android/i) ||
      navigator.userAgent.match(/webOS/i) ||
      navigator.userAgent.match(/iPhone/i) ||
      navigator.userAgent.match(/iPod/i)
        ? 'mobile'
        : 'pc';
    const accountData = userData.session.account;
    const isLoggedIn = userData.session.loggedIn;
    const pageTitle = pageData.node.nodeType !== 'error' ? pageData.node.name : 'Error 404';

    let pageViewData = {
      event: 'virtualPageview',
      pagePath: customUrl || window.location.pathname,
      pageTitle: pageTitle, // getting node name or empty string
      pageType: pageData.node.nodeType && getPageType(pageData.node.nodeType),
      device: userDevice,
      geo: userData.locale === 'en_AU@AUD' ? 'AU' : 'NZ', // get user Location from local storage if
      userStatus: isLoggedIn ? 'Authenticated' : 'Anonymous',
    };

    if (isLoggedIn) {
      pageViewData = {
        ...pageViewData,
        customerId: accountData?.accountId,
        customerStatus: 'existing',
        order_count: accountData?.projectSpecificData?.ordersCount || 0,
      };
    }

    this.push(pageViewData);
  };

  cartContentsPush = async (cartData) => {
    if (!cartData) return;
    const { totalItemsCount } =
      cartData.lineItems &&
      cartData.lineItems.reduce(
        (acc, elem) => {
          // If we have discount then let's calculate discount
          return {
            totalItemsCount: acc.totalItemsCount + elem.count,
          };
        },
        { totalItemsCount: 0 },
      );

    const cartItemsData =
      cartData?.lineItems.length > 0
        ? cartData?.lineItems?.map((item) => {
            return {
              id: item.lineItemId,
              sku: item.variant?.sku,
              quantity: item.count,
              price:
                item.discountedPrice && item.discountedPrice !== 0
                  ? formatPrice(item.discountedPrice)
                  : formatPrice(item.price),
            };
          })
        : [];

    const lastItemFromStorage = getItem('lastAddedItem');
    const lastCurrentEvent = this.getDataLayer()
      ?.reverse()
      .find((x) => x.event === 'cartContentsPush');
    const lastItem = lastCurrentEvent && cartItemsData && diffArrays(cartItemsData, lastCurrentEvent.cartItems, 'id');

    this.push({
      event: 'cartContentsPush',
      cartTotal: cartData.sum ? formatPrice(cartData.sum) : null,
      cartNumItems: totalItemsCount || cartData?.lineItems?.length,
      cartItems: cartItemsData,
      latestCartItem: lastItem
        ? [
            {
              id: lastItem.id,
              sku: lastItem.sku,
              quantity: lastItem.quantity,
              price: lastItem.price,
            },
          ]
        : lastItemFromStorage
          ? { ...JSON.parse(lastItemFromStorage) }
          : [],
    });
  };

  loginEvent = async (data) => {
    this.push({
      event: 'login',
    });
    this.customerDetails(data);
  };

  createAccountEvent = async (data) => {
    this.customerDetails(data);
    this.push({
      event: 'createAccount',
    });
  };

  logoutEvent = () => {
    this.push({
      event: 'logout',
    });
  };

  newsLetterSubscribe = async (email) => {
    this.push({
      event: 'newsletterSubscribe',
      email: email,
      em: await encryptToSha256(email),
    });
  };

  customerDetails = async ({ account, loggedIn, userAddress = {} }) => {
    if (!account) return;
    const hasDefaultAddress = account?.addresses?.find(
      (adr) => adr.isDefaultShippingAddress && adr.isDefaultBillingAddress,
    );
    const birthdayToDate = new Date(account.birthday).toLocaleDateString().split('/');

    const removeFalsyValues = (obj) => {
      const newObj = {};
      Object.keys(obj).forEach((key) => {
        if (obj[key]) newObj[key] = obj[key];
      });
      return newObj;
    };
    const formatPhone = (phone) => phone && phone.replace(/[^0-9]/g, '');

    const city = hasDefaultAddress?.city?.toLowerCase() || userAddress?.city?.toLowerCase();
    const mobile_no = formatPhone(account?.phone || account?.projectSpecificData?.phone || userAddress?.phone);
    const zip = hasDefaultAddress?.postalCode || userAddress?.postalCode;
    const country = hasDefaultAddress?.country?.toLowerCase() || userAddress?.country?.toLowerCase();

    const customerData = {
      event: 'customerDetails',
      email: account?.email,
      em: account?.projectSpecificData?.email_sha256,
      customerId: account?.accountId,
      customerStatus: loggedIn ? 'existing' : 'non-customer',
      date_of_last_order:
        account?.projectSpecificData?.lastOrderDate && formatDate(account?.projectSpecificData?.lastOrderDate),
      customer_age: account?.birthday && getAge(account?.birthday),
      first_name: account?.firstName,
      fn: account?.projectSpecificData?.firstName_sha256 || (await encryptToSha256(account?.firstName)),
      last_name: account?.lastName,
      ln: account?.projectSpecificData?.lastName_sha256 || (await encryptToSha256(account?.lastName)),
      city: city,
      ct:
        (hasDefaultAddress && hasDefaultAddress?.projectSpecificData?.city_sha256) ||
        (city && (await encryptToSha256(city))),
      mobile_no,
      ph: mobile_no && (await encryptToSha256(mobile_no)),
      zip,
      zp:
        (hasDefaultAddress && hasDefaultAddress?.projectSpecificData?.zip_sha256) ||
        (zip && (await encryptToSha256(zip))),
      country_code: country,
      country:
        (hasDefaultAddress && hasDefaultAddress?.projectSpecificData?.country_sha256) ||
        (country && (await encryptToSha256(country))),
      birthday: account?.birthday && birthdayToDate?.reverse().join('-'),
    };
    this.push(removeFalsyValues(customerData));
  };

  guestDetails = async ({ data }) => {
    if (!data) return;

    const removeFalsyValues = (obj) => {
      const newObj = {};
      Object.keys(obj).forEach((key) => {
        if (obj[key]) newObj[key] = obj[key];
      });
      return newObj;
    };

    const formatPhone = (phone) => phone && phone.replace(/[^0-9]/g, '');
    const mobile_no = formatPhone(data.billingAddress?.phone || data.shippingAddress?.phone);

    const firstName = data?.firstName || data?.billingAddress?.firstName || data?.shippingAddress?.firstName;
    const lastName = data?.lastName || data?.billingAddress?.lastName || data?.shippingAddress?.lastName;
    const city = data?.billingAddress?.city?.toLowerCase() || data?.shippingAddress?.city?.toLowerCase();
    const zip = data?.billingAddress?.postalCode || data?.shippingAddress?.postalCode;
    const country = data?.billingAddress?.country.toLowerCase() || data?.shippingAddress?.country.toLowerCase();

    const guestData = {
      event: 'customerDetails',
      email: data?.email,
      em: data?.email && (await encryptToSha256(data?.email)),
      customerId: data?.accountId,
      customerStatus: 'non-customer',
      first_name: firstName,
      fn: firstName && (await encryptToSha256(firstName)),
      last_name: lastName,
      ln: lastName && (await encryptToSha256(lastName)),
      city: city,
      ct: city && (await encryptToSha256(city)),
      mobile_no,
      ph: mobile_no && (await encryptToSha256(mobile_no)),
      zip: zip,
      zp: zip && (await encryptToSha256(zip)),
      country_code: country,
      country: country && (await encryptToSha256(country)),
    };

    this.push(removeFalsyValues(guestData));
  };

  productClick = (product, placement = null, listName = null) => {
    const { parentProduct } = product;
    const primaryCategory =
      parentProduct.projectSpecificData?.primaryCategory?.name ||
      parentProduct.variants?.[0]?.projectSpecificData?.primaryCategory?.name ||
      parentProduct.projectSpecificData?.primaryCategory ||
      parentProduct.variants?.[0]?.projectSpecificData?.primaryCategory;
    this.push({
      event: 'productClick',
      ecommerce: {
        currencyCode: product.currencyCode || product.variants[0].currency,
        click: {
          actionField: {
            list: `${placement && placement}${listName ? ' | ' + listName : ''}`,
          },
          products: [
            {
              id: parentProduct.projectSpecificData?.key,
              name: product.name,
              price: formatPrice(product.price || product.variants?.[0]?.price),
              brand: getItem('brand'),
              category: primaryCategory || product.categoryTitle || '',
              position: product.position,
            },
          ],
        },
      },
    });
  };

  productDetail = (product, selectedVariant) => {
    const variant = selectedVariant || product?.projectSpecificData.extendedSizes[0];

    const isAUD = variant?.currency === 'AUD';
    const isNZD = variant?.currency === 'NZD';

    if (!variant) {
      return;
    }

    this.push({
      event: 'productDetail',
      ecommerce: {
        currencyCode: variant?.currency,
        detail: {
          products: [
            {
              id: variant?.projectSpecificData?.productKey,
              name: variant?.projectSpecificData?.name,
              variant: variant?.sku,
              price: getCurrentPrice(variant),
              brand: getItem('brand'),
              category: variant?.projectSpecificData?.primaryCategory,
              dimension16: variant?.isOnStock ? 'Yes' : 'No',
              dimension14: variant?.attributes?.ean,
              dimension15: getHasSalePrice(variant?.projectSpecificData?.prices) ? 'Y' : 'N',
              dimension17: formatPrice(variant?.price),
              dimension18: variant?.images?.[0]?.url,
              product_range: variant?.attributes?.productrange?.label,
              product_type: variant?.attributes?.producttype?.label,
              on_sale: isNZD ? variant?.attributes?.onsalenz : isAUD ? variant?.attributes?.onsaleau : false,
            },
          ],
        },
      },
    });
  };

  productImpressions = (productList, listName) => {
    // Because our product list contains promo tile with custom data strucutre, we need to filter it out
    const fistProductVariant =
      productList &&
      productList.find((item) => {
        return item.type !== 'promo';
      });
    // Filtering out promo products
    const filteredImpressions =
      productList &&
      productList.filter((item) => {
        return item.type !== 'promo';
      });

    const itemBatches = batchItems(filteredImpressions);

    itemBatches?.forEach((batchItems) => {
      this.push({
        event: 'productImpressions',
        ecommerce: {
          currencyCode: fistProductVariant?.attributes?.currency || fistProductVariant?.variants?.[0].currency,
          impressions: batchItems?.map((product) => {
            const initialVariant = product?.variants?.length && product.variants[0];
            let category =
              product.projectSpecificData?.primaryCategory?.name ||
              product.variants?.[0]?.projectSpecificData?.primaryCategory?.name ||
              product.projectSpecificData?.primaryCategory ||
              product.variants?.[0]?.projectSpecificData?.primaryCategory;
            const primaryCategoryAttribute = product.attributes?.find((attr) => attr.key === 'primaryCategory');
            if (!category && primaryCategoryAttribute) {
              category = JSON.parse(primaryCategoryAttribute.value).name;
            }

            return {
              id: product.projectSpecificData?.key,
              name: product.name,
              price: formatPrice(initialVariant?.price || product.price),
              brand: getItem('brand'), // getting brand from localStorage
              category: category || '',
              position: product.position,
              list: `Listing Page | ${listName}`,
            };
          }),
        },
      });
    });
  };

  productImpressionsDynamic = (productList, listName, currency) => {
    // Filtering out promo products
    const filteredImpressions =
      productList &&
      productList.filter((item) => {
        return item.type !== 'promo';
      });

    const itemBatches = batchItems(filteredImpressions);
    const currencyCode =
      currency ||
      productList?.variants?.[0]?.currency ||
      productList?.[0]?.variants?.[0]?.currency ||
      productList?.[0]?.projectSpecificData?.prices?.[0]?.value?.currencyCode;

    itemBatches?.forEach((batchItems) => {
      this.push({
        event: 'productImpressions',
        ecommerce: {
          currencyCode,
          impressions: batchItems?.map((product, index) => {
            const matchedProductKey = product?.skus?.[0]?.name?.match(/(.*?-.*?)-.*/);
            let category =
              product.projectSpecificData?.primaryCategory?.name ||
              product.variants?.[0]?.projectSpecificData?.primaryCategory?.name ||
              product.projectSpecificData?.primaryCategory ||
              product.variants?.[0]?.projectSpecificData?.primaryCategory;
            const primaryCategoryAttribute = product.skus?.[0]?.attributes?.find(
              (attr) => attr.key === 'primarycategory',
            );
            if (!category && primaryCategoryAttribute) {
              category = JSON.parse(primaryCategoryAttribute.value).name;
            }
            const primaryCategoryCustomField = product?.custom_fields?.primarycategory;
            if (!category && primaryCategoryCustomField) {
              category = primaryCategoryCustomField;
            }

            return {
              id: matchedProductKey?.[1] || product.slug?.toUpperCase(),
              name: product.productName,
              price: formatPrice(product.regularPrice),
              brand: getItem('brand'),
              category: category || '',
              position: index + 1,
              list: listName,
            };
          }),
        },
      });
    });
  };

  searchImpressions = (productList, searchTerm, searchResultCount) => {
    if (!productList?.length) return;

    this.push({
      event: 'searchImpressions',
      searchTerm,
      searchResultCount,
      ecommerce: {
        currencyCode: productList[0]?.currency || productList[0]?.variants?.[0]?.attributes?.currency,
        impressions: productList.map((product, index) => {
          return {
            id: product.slug?.toUpperCase(),
            name: product.name,
            price: formatPrice(product.price || product.variants?.[0]?.price / 100),
            brand: getItem('brand'),
            category:
              product.projectSpecificData?.primaryCategory?.name ||
              product.variants?.[0]?.projectSpecificData?.primaryCategory?.name ||
              product.categories?.[0],
            position: index + 1,
            list: `Listing Page | Search`,
          };
        }),
      },
    });
  };

  addToCart = (product, selectedVariant, quantity, cartData, currency) => {
    // Getting product key from sku
    const matchedProductKey = selectedVariant.sku.match(/(.*?-.*?)-.*/);

    const category =
      product.projectSpecificData?.primaryCategory?.name ||
      selectedVariant.projectSpecificData?.primaryCategory ||
      product.variants?.[0]?.projectSpecificData?.primaryCategory;

    const hasImages = selectedVariant.images?.length;

    const image =
      typeof selectedVariant.images?.[0] === 'string' ? selectedVariant.images?.[0] : selectedVariant.images?.[0]?.url;

    const mergedImage = hasImages ? image : selectedVariant.imageUrl || selectedVariant.image_url;

    if (product && selectedVariant) {
      setItem(
        'lastAddedItem',
        JSON.stringify({
          id: matchedProductKey?.[1],
          sku: selectedVariant.sku,
          price: getCurrentPrice(selectedVariant),
          quantity,
        }),
      );
    }

    this.push({
      event: 'addToCart',
      ecommerce: {
        currencyCode: product.variants[0].currency || currency,
        add: {
          products: [
            {
              id: matchedProductKey?.[1] || null,
              name: product.name,
              variant: selectedVariant.sku,
              price: getCurrentPrice(selectedVariant),
              brand: getItem('brand'),
              category: category || '',
              dimension16: selectedVariant.isOnStock ? 'Yes' : 'No',
              dimension14: selectedVariant.attributes?.ean,
              dimension15: getHasSalePrice(selectedVariant.projectSpecificData.prices) ? 'Y' : 'N',
              dimension17: formatPrice(product.price),
              dimension18: mergedImage || '',
              quantity,
            },
          ],
        },
      },
    });
    this.cartContentsPush(cartData);
  };

  removeFromCart = (removedItem, cartData) => {
    const category = removedItem.projectSpecificData?.primaryCategory;
    const productKey = removedItem.projectSpecificData?.productKey;

    const image =
      typeof removedItem?.variant?.images?.[0] === 'string'
        ? removedItem?.variant?.images?.[0]
        : removedItem?.variant?.images?.[0]?.url;

    this.push({
      event: 'removeFromCart',
      ecommerce: {
        currencyCode: removedItem.currency,
        remove: {
          products: [
            {
              id: productKey,
              name: removedItem.name,
              variant: removedItem.sku || removedItem.variant?.sku,
              price: getCurrentPrice(removedItem),
              brand: getItem('brand'),
              ...(category && { category: category || '' }),
              dimension16: removedItem.variant?.isOnStock ? 'Yes' : 'No',
              dimension14: removedItem?.variant?.attributes?.ean,
              dimension15: getHasSalePrice(removedItem.projectSpecificData.prices) ? 'Y' : 'N',
              dimension17: formatPrice(removedItem.price),
              dimension18: image || '',
              quantity: 1,
            },
          ],
        },
      },
    });
    this.cartContentsPush(cartData);
  };

  addToWishlist = (addedItem) => {
    const matchedProductKey = addedItem.sku.match(/(.*?-.*?)-.*/);
    const imageUrl = addedItem.imageUrl || addedItem.images?.[0]?.url;
    const image = addedItem?.images && typeof addedItem?.images[0] === 'string' ? addedItem?.images[0] : imageUrl;

    const category =
      addedItem.projectSpecificData?.primaryCategory ||
      addedItem.projectSpecificData?.primaryCategory?.name ||
      addedItem.variants?.[0]?.projectSpecificData?.primaryCategory;
    this.push({
      event: 'addToWishlist',
      ecommerce: {
        currencyCode: addedItem.currency,
        wishlist: {
          products: [
            {
              id: matchedProductKey?.[1] || addedItem.sku,
              name: addedItem.name,
              variant: addedItem.sku,
              price: getCurrentPrice(addedItem),
              brand: getItem('brand'),
              category: category || '',
              dimension16: addedItem?.isOnStock ? 'Yes' : 'No',
              dimension14: addedItem?.attributes?.ean || null,
              dimension15: getHasSalePrice(addedItem?.projectSpecificData?.prices) ? 'Y' : 'N',
              dimension17: formatPrice(addedItem.price),
              dimension18: image || '',
              quantity: addedItem.count,
            },
          ],
        },
      },
    });
  };

  promoCodeAttempt = ({ promoCode, cartItem, promoCodeResult, amount }) => {
    this.push({
      event: 'promoCodeAttempt',
      promoCode,
      promoCodeResult,
      discount: formatPrice(amount),
    });
  };

  checkoutStep = (step, productArray, origin) => {
    this.push({
      event: 'checkout',
      ecommerce: {
        checkout: {
          actionField: { step },
          products: productArray?.map((product) => {
            const isAUD = product?.variant?.currency === 'AUD';
            const isNZD = product?.variant?.currency === 'NZD';
            const matchedProductKey = product.variant.sku.match(/(.*?-.*?)-.*/);
            const mergedProductKey = product.projectSpecificData?.productKey?.toUpperCase() || matchedProductKey?.[1];
            const image =
              typeof product.variant.images[0] === 'string'
                ? product.variant.images[0]
                : product.variant.images[0]?.url;

            const url = product.projectSpecificData?.url;
            return {
              id: mergedProductKey,
              name: product.name,
              variant: product.variant.sku,
              price: getCurrentPrice(product),
              brand: getItem('brand'),
              category:
                product.projectSpecificData?.primaryCategory?.name ||
                product.variants?.[0]?.projectSpecificData?.primaryCategory?.name ||
                product.projectSpecificData?.primaryCategory ||
                product.variants?.[0]?.projectSpecificData?.primaryCategory ||
                '',
              dimension16: product.variant.isOnStock ? 'Yes' : 'No',
              dimension14: product.variant?.attributes?.ean,
              dimension15: getHasSalePrice(product.projectSpecificData.prices) ? 'Y' : 'N',
              dimension17: formatPrice(product.price),
              dimension18: image || '',
              dimension19: url ? origin + url : '',
              quantity: product.count,
              product_range: product.variant?.attributes?.productrange?.label,
              product_type: product.variant?.attributes?.producttype?.label,
              on_sale: isNZD
                ? product.variant?.attributes?.onsalenz
                : isAUD
                  ? product.variant?.attributes?.onsaleau
                  : false,
            };
          }),
        },
      },
    });
  };

  checkoutOption = (step, option) => {
    this.push({
      event: 'checkoutOption',
      ecommerce: {
        checkout_option: {
          actionField: {
            step,
            option,
          },
        },
      },
    });
  };

  purchase = async (order, account, isClickAndCollect) => {
    const { discountCodesOriginal, discounts } = order?.projectSpecificData || {};

    const appliedDiscount = order?.discountCodes?.[0];

    const originalDiscount =
      appliedDiscount && discountCodesOriginal?.find(({ id }) => id === appliedDiscount.discountId);

    const discount =
      originalDiscount && discounts?.filter((d) => d.type === 'cart-discount' && d.id === originalDiscount.originalId);

    const discountFinalAmount = discount?.[0]?.amount || 0;

    const sumTax = order?.lineItems.find((item) => item.projectSpecificData?.tax)
      ? order?.lineItems?.reduce((acc, val) => acc + val.projectSpecificData.tax, 0)
      : order?.taxed?.taxPortions.reduce((acc, val) => acc + val.amount, 0);

    // Order success page
    // push guest specific data into customerDetails datalayer
    if (!account?.email) {
      await this.guestDetails({
        data: {
          ...order,
          accountId: account?.accountId,
        },
      });
    }

    this.push({
      event: 'purchase',
      ecommerce: {
        purchase: {
          actionField: {
            id: order.orderId,
            revenue: formatPrice(order.projectSpecificData.original_total),
            tax: formatPrice(sumTax),
            shipping: formatPrice(order.shippingInfo.price),
            coupon: order.discountCodes[0]?.code,
            promoApplied: order.projectSpecificData.discounts?.map((x) => x.name),
            couponDiscount: formatPrice(discountFinalAmount),
            orderZip: order.shippingAddress?.postalCode,
            orderPayment: getItem('paymentType'),
            orderDelivery: isClickAndCollect ? 'Collect In Store' : 'Home Delivery',
            customerType: account?.projectSpecificData?.ordersCount > 0 ? 'repeat-customer' : 'new-customer',
          },
          products: order.lineItems.map((product) => {
            const isAUD = product?.variant?.currency === 'AUD';
            const isNZD = product?.variant?.currency === 'NZD';
            const matchedProductKey = product.variant.sku.match(/(.*?-.*?)-.*/);
            return {
              id: matchedProductKey?.[1] || product.variant.sku,
              name: product.name,
              variant: product.variant.sku,
              price: getCurrentPrice(product),
              brand: getItem('brand'),
              category:
                product.projectSpecificData?.primaryCategory?.name ||
                product.variants?.[0]?.projectSpecificData?.primaryCategory?.name ||
                product.projectSpecificData?.primaryCategory ||
                product.variants?.[0]?.projectSpecificData?.primaryCategory ||
                '',
              dimension16: product.variant.isOnStock ? 'Yes' : 'No',
              dimension14: product.variant?.attributes?.ean,
              dimension15: getHasSalePrice(product.projectSpecificData.prices) ? 'Y' : 'N',
              dimension17: formatPrice(product.price),
              dimension18: product.variant.images[0] || '',
              quantity: product.count,
              product_range: product?.variant?.attributes?.productrange?.label,
              product_type: product?.variant?.attributes?.producttype?.label,
              on_sale: isNZD
                ? product?.variant?.attributes?.onsalenz
                : isAUD
                  ? product?.variant?.attributes?.onsaleau
                  : false,
            };
          }),
        },
      },
    });
    removeItem('paymentType');
  };

  algoliaHitsViewed = () => {
    this.push({ event: 'Hits Viewed' });
  };

  push = (data) => {
    const dataLayer = this.getDataLayer();
    if (!dataLayer) {
      return;
    }
    dataLayer.push(data);
  };

  getDataLayer = () => {
    if (typeof window === 'undefined' || typeof window[this.dataLayerIdentifier] === 'undefined') {
      console.warn('Data layer with identifier "' + this.dataLayerIdentifier + '" could not be accessed');
      return null;
    }

    return window[this.dataLayerIdentifier] || [];
  };
}

export default new TagManager();
